import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Award, ShieldCheck, DollarSign, Truck, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, whyChooseUs, globalRegions, WHATSAPP_DEFAULT } from '../mock';

const Home = () => {
  const iconMap = {
    'award': Award,
    'shield-check': ShieldCheck,
    'dollar-sign': DollarSign,
    'truck': Truck
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Agro Corp India – Trusted Exporter of Premium Natural Agro Products from India</title>
        <meta name="description" content="Agro Corp India is a leading wholesale exporter of premium agricultural products from Pune, India. Basmati rice, cane jaggery, dehydrated products for worldwide export. Request bulk quote." />
        <meta name="keywords" content="agro products exporter India, wholesale agricultural supplier Pune, B2B food export India, basmati rice exporter, jaggery supplier worldwide, dehydrated products wholesale" />
        <link rel="canonical" href="https://www.agrocorpindia.com/" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1529313780224-1a12b68bed16')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className="space-y-6 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Agro Corp India
            </h1>
            <p className="text-2xl md:text-3xl text-amber-400 font-semibold">
              Trusted Exporter of Natural Agro Products
            </p>
            <p className="text-xl md:text-2xl text-white font-light max-w-3xl mx-auto">
              Quality | Purity | Reliability
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
              <Link to="/products">
                <Button data-testid="hero-explore-products-btn" className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  Explore Products
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer">
                <Button data-testid="hero-whatsapp-btn" className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Inquire on WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-20 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full">
                  About Us
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-green-900 leading-tight">
                Welcome to Agro Corp India
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Agro Corp India was established in 2025 in Pune, Maharashtra, with a vision to supply high-quality natural and processed agricultural products to global markets. We collaborate with certified partner facilities for storage, processing, and packaging to ensure product quality and compliance with international standards.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-900">Premium Quality Products</h4>
                    <p className="text-gray-600">Sourced from the finest farms and processed with care</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-900">International Standards</h4>
                    <p className="text-gray-600">All products meet global quality and safety requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-900">Worldwide Export</h4>
                    <p className="text-gray-600">Building lasting partnerships with buyers across the globe</p>
                  </div>
                </div>
              </div>
              <Link to="/about">
                <Button className="bg-green-700 hover:bg-green-800 text-white px-6 py-3 mt-4">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1566735049879-444f282d77f9"
                alt="Agricultural field"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-4 border-green-100">
                <div className="flex items-center space-x-3">
                  <Sparkles className="w-10 h-10 text-amber-500" />
                  <div>
                    <p className="text-3xl font-bold text-green-900">2025</p>
                    <p className="text-sm text-gray-600">Established</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">
              Our Products
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mb-4">
              Premium Quality Exports
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our range of natural and processed agricultural products for worldwide export
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.slice(0, 8).map((product) => (
              <Card
                key={product.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-green-200"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {product.category}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-green-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{product.shortDescription}</p>
                  <Link to={`/products/${product.id}`}>
                    <Button data-testid={`home-inquire-btn-${product.id}`} className="w-full bg-green-700 hover:bg-green-800 text-white transition-all duration-300">
                      Inquire Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button data-testid="view-all-products-btn" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mb-4">
              Your Trusted Export Partner
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We deliver excellence through quality, service, and reliability
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card
                  key={item.id}
                  className="text-center p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-green-200 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-green-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Markets Teaser */}
      <section className="py-20 bg-gradient-to-br from-green-800 to-green-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Exporting to Global Markets
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Building partnerships with international buyers across continents
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {globalRegions.map((region) => (
              <div key={region.id} className="bg-white/10 backdrop-blur-sm px-6 py-4 rounded-lg border border-white/20">
                <p className="text-2xl font-bold">{region.region}</p>
              </div>
            ))}
          </div>
          <Link to="/export-markets">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3">
              View Global Reach
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-green-700 to-green-800 rounded-3xl p-12 md:p-16 text-center shadow-2xl">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Partner With Us?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Get in touch today to discuss your bulk requirements and receive a customized export quote
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer">
                <Button data-testid="cta-whatsapp-btn" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Inquire on WhatsApp
                </Button>
              </a>
              <Link to="/products">
                <Button className="bg-white hover:bg-gray-100 text-green-800 px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  Browse Products
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
